import { ActionIcon, Badge, Group, Paper, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconUser, IconUserMinus, IconUsers } from '@tabler/icons-react';
import { HOST_SEAT_ROLE_LABEL, type SpreadParticipantView } from '@creativitas/shared';
import { pluralize } from '@/lib/plural';

/**
 * Список участников. Ведущему он же и пульт: сколько карт у каждого, кто
 * вытянул только что, кого убрать из сессии. Участнику и игре достаточно имён
 * и статуса — всё остальное здесь необязательно и без пропсов не показывается.
 */
export function SessionParticipants({
  participants,
  cardCounts,
  recentIds,
  onRemove,
  busy = false,
  count,
  countLabel,
}: {
  participants: SpreadParticipantView[];
  /** Что считать в бейдже; по умолчанию — все строки списка. */
  count?: number;
  /**
   * Чем подписано число. В партии список и счётчик расходятся: ведущий в
   * списке есть, а в числе его нет, пока он не сел за стол. Подпись
   * «Игроков» и объясняет расхождение — без неё число выглядело ошибкой.
   */
  countLabel?: string;
  /** Сколько карт у каждого — только в МАК-сессии у ведущего. */
  cardCounts?: Record<string, number>;
  /** Кто вытянул карту за последние секунды: отметка «только что». */
  recentIds?: ReadonlySet<string>;
  /** Удалить участника; кнопка есть только у гостей и только когда передан обработчик. */
  onRemove?: (participant: SpreadParticipantView) => void;
  busy?: boolean;
}) {
  return (
    <Paper withBorder p="md" data-testid="session-participants">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <IconUsers size={18} color="var(--accent)" />
          <Text fw={700}>Участники</Text>
        </Group>
        <Badge variant="light" data-testid="participants-count">
          {countLabel ? `${countLabel}: ${count ?? participants.length}` : (count ?? participants.length)}
        </Badge>
      </Group>
      <Stack gap="xs">
        {participants.map((participant) => (
          <Group key={participant.id} justify="space-between" data-testid="session-participant">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color={participant.isConnected ? 'teal' : 'gray'}>
                <IconUser size={13} />
              </ThemeIcon>
              <Text size="sm">{participant.displayName}</Text>
              {participant.kind === 'facilitator' ? (
                <Badge size="xs" variant="outline">Ведущий</Badge>
              ) : participant.kind === 'user' ? (
                /* Ведущий сел за собственный стол: он и ведёт, и играет. */
                <Badge size="xs" variant="light">{HOST_SEAT_ROLE_LABEL}</Badge>
              ) : null}
              {recentIds?.has(participant.id) ? (
                <Badge size="xs" color="gold" variant="filled" data-testid="participant-recent-draw">
                  только что
                </Badge>
              ) : null}
            </Group>
            <Group gap="xs">
              {cardCounts ? (
                <Text size="xs" c="var(--text-muted)" data-testid="participant-card-count">
                  {pluralize(cardCounts[participant.id] ?? 0, 'карта', 'карты', 'карт')}
                </Text>
              ) : null}
              <Badge size="xs" color={participant.isConnected ? 'teal' : 'gray'} variant="dot">
                {participant.isConnected ? 'в сети' : 'не в сети'}
              </Badge>
              {onRemove && participant.kind === 'guest' ? (
                <Tooltip label="Удалить из сессии">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    disabled={busy}
                    aria-label={`Удалить участника ${participant.displayName}`}
                    onClick={() => onRemove(participant)}
                  >
                    <IconUserMinus size={15} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
            </Group>
          </Group>
        ))}
      </Stack>
    </Paper>
  );
}
